/**
 * PlayCanvas streamed-SOG / lod-streaming tuning presets.
 */
import type { PlayCanvasLodOverrides } from '@viewer/world/worldTypes';

/** Camera vertical FOV (degrees) used by the PlayCanvas viewer and entrance reveal. */
export const CAMERA_FOV = 65;

export const DESKTOP_LOD_SPLAT_COUNT = 8_000_000;
/**
 * Default hard cap on rendered splats for mobile/touch devices. Worlds may raise or
 * lower this via `mobileLodSplatCount` in world.json.
 */
export const MOBILE_LOD_SPLAT_COUNT = 1_200_000;

/** Target streaming fill before colour burst (PlayCanvas SOG). */
export const PLAYCANVAS_STREAMING_FILL = 0.95;

export interface PlayCanvasLodPreset {
  lodRangeMin: number;
  lodRangeMax: number;
  lodBaseDistance: number;
  lodMultiplier: number;
  splatBudgetMillions: number;
  lodUpdateAngle: number;
  lodBehindPenalty: number;
  lodUpdateDistance: number;
  lodUnderfillLimit: number;
  radialSorting: boolean;
  /** GSplat entity local Euler X (degrees) for PlayCanvas-authored SOGs. */
  orientationEulerX: number;
  /** Screen-pixel size floor; lower keeps distant coarse splats visible. */
  minPixelSize: number;
  /** Compute renderer contribution cull; 0 disables (needed for far-field base). */
  minContribution: number;
}

export const PLAYCANVAS_DESKTOP_PRESET: PlayCanvasLodPreset = {
  lodRangeMin: 0,
  lodRangeMax: 3,
  lodBaseDistance: 5,
  lodMultiplier: 4,
  splatBudgetMillions: 6,
  lodUpdateAngle: 90,
  lodBehindPenalty: 5,
  lodUpdateDistance: 1,
  lodUnderfillLimit: 10,
  radialSorting: true,
  orientationEulerX: 180,
  minPixelSize: 1,
  minContribution: 0,
};

export const PLAYCANVAS_MOBILE_PRESET: PlayCanvasLodPreset = {
  lodRangeMin: 2,
  lodRangeMax: 3,
  lodBaseDistance: 2,
  lodMultiplier: 2,
  splatBudgetMillions: 1,
  lodUpdateAngle: 90,
  lodBehindPenalty: 3,
  lodUpdateDistance: 0.5,
  lodUnderfillLimit: 5,
  radialSorting: true,
  orientationEulerX: 180,
  minPixelSize: 1,
  minContribution: 0,
};

/**
 * Merge world.json `lod` overrides onto a device preset.
 *
 * On mobile, `lodMobile` overrides (if present) are layered last so a world can
 * tune phones independently of the desktop `lod` block.
 */
export function mergePlayCanvasLodPreset(
  base: PlayCanvasLodPreset,
  overrides?: PlayCanvasLodOverrides,
  mobileOverrides?: PlayCanvasLodOverrides,
  isMobile = false,
): PlayCanvasLodPreset {
  let merged = base;
  if (overrides) merged = { ...merged, ...overrides };
  if (isMobile && mobileOverrides) merged = { ...merged, ...mobileOverrides };
  return merged;
}
